import type { AbortSignalWithAny } from '@/types/web-api'
import type { GenericAbortSignal } from 'axios'
import { isConsoleDeployment, isPlatformDeployment } from './deployment'

export type ApiClientType = 'api' | 'owner' | 'console'

/**
 * Check if an API client is allowed on the current deployment
 *
 * Client access rules:
 * - 'console' client → Allowed on Console (billing, plans, etc.)
 * - 'api' client → Platform only (project-level: devices, actions, streams)
 * - 'owner' client → Platform only (org-level: projects, tenants management)
 *
 */
export function shouldBlockClient(clientType: ApiClientType): boolean {
	// Console deployment: only 'console' client is allowed
	// Platform-specific clients (api, owner) are blocked
	if (isConsoleDeployment()) {
		if (clientType === 'api' || clientType === 'owner') {
			console.error(
				`[DeploymentGuard] Blocked ${clientType} client. Platform-specific APIs are not available on Console deployments.`,
			)
			return true
		}
	}

	// Platform deployment: 'console' client is blocked (billing/plans are console-only)
	if (isPlatformDeployment()) {
		if (clientType === 'console') {
			console.error(
				`[DeploymentGuard] Blocked ${clientType} client. Console-specific APIs (billing, plans) are not available on Platform deployments.`,
			)
			return true
		}
	}

	return false
}

/**
 * Apply deployment guard to axios config based on client type
 * If the client is not allowed on current deployment, silently aborts the request
 */
export function applyDeploymentGuard(clientType: ApiClientType, config: { signal?: GenericAbortSignal }): boolean {
	const isBlocked = shouldBlockClient(clientType)
	if (isBlocked) {
		// Silently abort the request - error is already logged in shouldBlockClient
		const controller = new AbortController()
		controller.abort()
		// Merge signals if AbortSignal.any is available (modern browsers).
		// This preserves the original signal mechanism while enforcing our abort.
		const signal = AbortSignal as AbortSignalWithAny
		if (config.signal && typeof signal.any === 'function') {
			config.signal = signal.any([config.signal as unknown as AbortSignal, controller.signal])
		} else {
			// Fallback: If AbortSignal.any is missing or no original signal exists,
			// just use our already-aborted signal.
			config.signal = controller.signal
		}
		return true
	}
	return false
}
